import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Medium } from '../types';

export interface ChatWindowState {
    mediumId: number;
    medium: Medium;
    isMinimized: boolean;
    unreadCount: number;
    lastActiveTimestamp: number;
}

interface ChatLayoutContextType {
    chatSessions: ChatWindowState[];
    activeChatId: number | null;
    setActiveChat: (mediumId: number | null) => void;
    openChat: (medium: Medium, initialMinimized?: boolean) => boolean;
    closeChat: (mediumId: number) => void;
    toggleMinimize: (mediumId: number) => void;
    setMinimized: (mediumId: number, isMinimized: boolean) => void;
    incrementUnread: (mediumId: number) => void;
    clearUnread: (mediumId: number) => void;
    isChatOpen: (mediumId: number) => boolean;
    getChatState: (mediumId: number) => ChatWindowState | undefined;
}

const STORAGE_KEY = 'astro_chat_layout_sessions_v1';

const ChatLayoutContext = createContext<ChatLayoutContextType | undefined>(undefined);

const loadInitialSessions = (): ChatWindowState[] => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
        if (saved && saved.trim() !== '') {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
                return parsed.filter(item => item && item.medium && item.medium.id);
            }
        }

        // Migration secours depuis 'astro_active_sessions'
        const legacySaved = sessionStorage.getItem('astro_active_sessions');
        if (legacySaved && legacySaved.trim() !== '') {
            const parsedLegacy = JSON.parse(legacySaved);
            if (Array.isArray(parsedLegacy)) {
                return parsedLegacy
                    .filter((m: any) => m && m.id)
                    .map((m: Medium) => ({
                        mediumId: m.id,
                        medium: m,
                        isMinimized: false,
                        unreadCount: 0,
                        lastActiveTimestamp: Date.now()
                    }));
            }
        }
    } catch (e) {
        console.error('Erreur chargement des sessions de chat:', e);
    }
    return [];
};

export const ChatLayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [chatSessions, setChatSessions] = useState<ChatWindowState[]>(loadInitialSessions);
    const [activeChatId, setActiveChatId] = useState<number | null>(() => {
        const initial = loadInitialSessions();
        return initial.length > 0 ? initial[initial.length - 1].mediumId : null;
    });

    // Synchroniser activeChatId si les sessions changent
    useEffect(() => {
        if (chatSessions.length === 0) {
            setActiveChatId(null);
        } else if (activeChatId === null || !chatSessions.some(s => s.mediumId === activeChatId)) {
            // Trouver la session la plus récemment active
            const active = [...chatSessions].sort((a, b) => b.lastActiveTimestamp - a.lastActiveTimestamp)[0];
            setActiveChatId(active ? active.mediumId : chatSessions[0].mediumId);
        }
    }, [chatSessions, activeChatId]);

    // Sauvegarde automatique de l'état du layout
    useEffect(() => {
        try {
            const dataStr = JSON.stringify(chatSessions);
            localStorage.setItem(STORAGE_KEY, dataStr);
            sessionStorage.setItem(STORAGE_KEY, dataStr);

            // Synchroniser avec la clef historique pour retro-compatibilité
            const legacyMediums = chatSessions.map(s => s.medium);
            sessionStorage.setItem('astro_active_sessions', JSON.stringify(legacyMediums));
        } catch (e) {
            console.error('Erreur sauvegarde layout chat:', e);
        }
    }, [chatSessions]);

    const setActiveChat = useCallback((mediumId: number | null) => {
        setActiveChatId(mediumId);
        if (mediumId !== null) {
            const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
            setChatSessions(prev => prev.map(s => {
                if (s.mediumId === mediumId) {
                    return { ...s, isMinimized: false, unreadCount: 0, lastActiveTimestamp: Date.now() };
                }
                // Sur mobile, si on active une session, minimiser les autres pour garder l'écran propre
                if (isMobile) {
                    return { ...s, isMinimized: true };
                }
                return s;
            }));
        }
    }, []);

    const openChat = useCallback((medium: Medium, initialMinimized = false): boolean => {
        let success = true;
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        
        setChatSessions(prev => {
            const existingIndex = prev.findIndex(s => s.mediumId === medium.id);
            if (existingIndex >= 0) {
                const updated = prev.map((s, idx) => {
                    if (idx === existingIndex) {
                        return {
                            ...s,
                            medium: medium,
                            isMinimized: initialMinimized,
                            unreadCount: 0,
                            lastActiveTimestamp: Date.now()
                        };
                    }
                    if (isMobile && !initialMinimized) {
                        return { ...s, isMinimized: true };
                    }
                    return s;
                });
                return updated;
            }

            let currentList = prev;
            // Si la limite de 4 conversations simultanées est atteinte, on libère automatiquement la plus ancienne sans bloquer l'utilisateur
            if (currentList.length >= 4) {
                const sorted = [...currentList].sort((a, b) => a.lastActiveTimestamp - b.lastActiveTimestamp);
                const oldestId = sorted[0].mediumId;
                currentList = currentList.filter(s => s.mediumId !== oldestId);
            }

            // Si mobile et nouvelle session non minimisée, minimiser les autres pour la lisibilité
            const preparedPrev = isMobile && !initialMinimized ? currentList.map(s => ({ ...s, isMinimized: true })) : currentList;

            return [
                ...preparedPrev,
                {
                    mediumId: medium.id,
                    medium,
                    isMinimized: initialMinimized,
                    unreadCount: 0,
                    lastActiveTimestamp: Date.now()
                }
            ];
        });

        if (success && !initialMinimized) {
            setActiveChatId(medium.id);
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('astro_close_popups'));
            }
        }

        return success;
    }, []);

    const closeChat = useCallback((mediumId: number) => {
        setChatSessions(prev => {
            const remaining = prev.filter(s => s.mediumId !== mediumId);
            return remaining;
        });
    }, []);

    const toggleMinimize = useCallback((mediumId: number) => {
        setChatSessions(prev => prev.map(s => {
            if (s.mediumId === mediumId) {
                const nextMinimized = !s.isMinimized;
                return {
                    ...s,
                    isMinimized: nextMinimized,
                    unreadCount: nextMinimized ? s.unreadCount : 0,
                    lastActiveTimestamp: Date.now()
                };
            }
            return s;
        }));
    }, []);

    const setMinimized = useCallback((mediumId: number, isMinimized: boolean) => {
        setChatSessions(prev => prev.map(s => {
            if (s.mediumId === mediumId) {
                return {
                    ...s,
                    isMinimized,
                    unreadCount: isMinimized ? s.unreadCount : 0,
                    lastActiveTimestamp: Date.now()
                };
            }
            return s;
        }));
    }, []);

    const incrementUnread = useCallback((mediumId: number) => {
        setChatSessions(prev => prev.map(s => {
            if (s.mediumId === mediumId && s.isMinimized) {
                return {
                    ...s,
                    unreadCount: (s.unreadCount || 0) + 1
                };
            }
            return s;
        }));
    }, []);

    const clearUnread = useCallback((mediumId: number) => {
        setChatSessions(prev => prev.map(s => {
            if (s.mediumId === mediumId) {
                return { ...s, unreadCount: 0 };
            }
            return s;
        }));
    }, []);

    const isChatOpen = useCallback((mediumId: number) => {
        return chatSessions.some(s => s.mediumId === mediumId);
    }, [chatSessions]);

    const getChatState = useCallback((mediumId: number) => {
        return chatSessions.find(s => s.mediumId === mediumId);
    }, [chatSessions]);

    return (
        <ChatLayoutContext.Provider value={{
            chatSessions,
            activeChatId,
            setActiveChat,
            openChat,
            closeChat,
            toggleMinimize,
            setMinimized,
            incrementUnread,
            clearUnread,
            isChatOpen,
            getChatState
        }}>
            {children}
        </ChatLayoutContext.Provider>
    );
};

export const useChatLayout = () => {
    const context = useContext(ChatLayoutContext);
    if (!context) {
        throw new Error('useChatLayout doit être utilisé au sein d\'un ChatLayoutProvider');
    }
    return context;
};
